import { useEffect, useState, useCallback } from 'react';
import { X, Send, Check, Trash2, RefreshCw, Loader2, Bell, GripVertical } from 'lucide-react';
import { isReallyQueued } from '../lib/issueDispatch';
import { api, OPEN_ISSUE_STATUSES } from '../api/client';
import type { GoalIssue } from '../api/types';
import './AgentIssues.css';

// Per-agent issue panel (Alex 2026-06-29) — the open issues/goal-issues assigned to
// this agent, with actions: Pushen (dispatch it to the agent to work now), Fertig
// (resolve), Verwerfen (wontfix). Reads/writes issue-api directly with the user bearer.

// forAssignee already filters server-side; kept as a belt-and-braces guard so a
// backend change can't quietly widen what this panel calls "open".
const OPEN_STATUSES = new Set<string>(OPEN_ISSUE_STATUSES);
const STAGE_SYM: Record<string, string> = { done: '●', in_progress: '◐', skipped: '⊘', pending: '○' };

export function AgentIssues({ sessionName, onClose }: { sessionName: string; onClose: () => void }) {
  const [items, setItems] = useState<GoalIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [flash, setFlash] = useState('');
  // Abarbeitungs-Reihenfolge der wartenden Issues (Issue #943). BEWUSST getrennt
  // von `items`: die Liste oben ist nach Erstellzeit sortiert, `queueOrder` ist
  // die Reihenfolge, in der der Dispatcher sie dem Agenten wirklich gibt.
  const [queueOrder, setQueueOrder] = useState<number[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.issues.forAssignee(`agent:${sessionName}`);
      setItems((r.items || []).filter((i) => OPEN_STATUSES.has(i.status)));
      setError('');
      // Die Warteschlange ist ein SEPARATER Aufruf und darf die Liste nicht
      // mitreissen, wenn sie fehlschlaegt — dann fehlt eben das Sortieren,
      // aber die Issues bleiben sicht- und bedienbar.
      try {
        const q = await api.dispatch.queue(`agent:${sessionName}`);
        setQueueOrder((q.queued || []).map((e: { issue_id: number }) => e.issue_id));
        setActiveId(q.active?.issue_id ?? null);
      } catch {
        setQueueOrder([]);
        setActiveId(null);
      }
    } catch {
      setError('Issues nicht erreichbar (issue-api).');
    } finally {
      setLoading(false);
    }
  }, [sessionName]);

  useEffect(() => { void load(); }, [load]);

  const act = async (id: number, fn: () => Promise<unknown>, ok?: string) => {
    setBusyId(id);
    setError('');
    try {
      await fn();
      if (ok) { setFlash(ok); window.setTimeout(() => setFlash(''), 2500); }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Aktion fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  // Single source of truth for the message the agent receives on "Pushen" — so
  // the preview shown in the row is byte-identical to what actually gets pushed.
  const pushPrompt = (i: GoalIssue) => `Bitte arbeite Issue #${i.id}: ${i.title}`;
  const push = (i: GoalIssue) =>
    act(i.id, () => api.sessions.sendInput(sessionName, pushPrompt(i)), `#${i.id} an ${sessionName} gepusht`);
  // Nudge a RUNNING issue (Issue #195): you can't always tell a dispatched issue
  // is still being worked — "Erinnern" pokes the agent to keep going.
  const remindPrompt = (i: GoalIssue) => `Bitte arbeite weiter an Issue #${i.id}: ${i.title}`;
  const remind = (i: GoalIssue) =>
    act(i.id, () => api.sessions.sendInput(sessionName, remindPrompt(i)), `Erinnerung an ${sessionName} für #${i.id}`);
  const done = (i: GoalIssue) =>
    act(i.id, () => api.issues.resolve(i.id, { commit: 'manual', summary: `Manuell als erledigt markiert (${sessionName})` }));
  const drop = (i: GoalIssue) =>
    act(i.id, () => api.issues.update(i.id, { status: 'wontfix' }));

  // Anzeige-Reihenfolge: erst das aktive Issue, dann die wartenden in
  // DISPATCH-Reihenfolge, dann alles Übrige (deferred, ohne Queue-Eintrag).
  // Ohne erreichbare Queue bleibt es bei der bisherigen Sortierung.
  const ordered = (() => {
    if (queueOrder.length === 0 && activeId == null) return items;
    const byId = new Map(items.map((i) => [i.id, i]));
    const out: GoalIssue[] = [];
    const seen = new Set<number>();
    const take = (id: number) => {
      const it = byId.get(id);
      if (it && !seen.has(id)) { out.push(it); seen.add(id); }
    };
    if (activeId != null) take(activeId);
    queueOrder.forEach(take);
    items.forEach((i) => { if (!seen.has(i.id)) out.push(i); });
    return out;
  })();

  const isQueued = (id: number) => queueOrder.includes(id);

  /** Verschiebt `dragged` an die Position von `target` und schickt die GANZE
   *  Liste — der Server prüft sie gegen den Ist-Zustand. */
  const applyReorder = async (draggedId: number, targetId: number) => {
    if (draggedId === targetId) return;
    const from = queueOrder.indexOf(draggedId);
    const to = queueOrder.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = [...queueOrder];
    next.splice(from, 1);
    next.splice(to, 0, draggedId);
    // Optimistisch zeichnen, damit das Ziehen sich nicht zäh anfühlt; bei
    // Konflikt wird gleich auf den Serverstand zurückgesetzt.
    const before = queueOrder;
    setQueueOrder(next);
    setReordering(true);
    setError('');
    try {
      const r = await api.dispatch.reorder(`agent:${sessionName}`, next);
      if (r.ok) {
        setQueueOrder((r.snapshot.queued || []).map((e) => e.issue_id));
        setFlash('Reihenfolge gespeichert');
        window.setTimeout(() => setFlash(''), 2000);
      } else if (r.conflict.error === 'queue_set_mismatch' && r.conflict.expected_issue_ids) {
        // Der Server liefert den Ist-Zustand mit — direkt übernehmen statt
        // einen zusätzlichen GET zu fahren.
        setQueueOrder(r.conflict.expected_issue_ids);
        setError('Warteschlange hat sich zwischenzeitlich geändert — neu geladen, bitte erneut ziehen.');
        await load();
      } else if (r.conflict.error === 'active_issue_not_reorderable') {
        setQueueOrder(before);
        setError('Das laufende Issue kann nicht verschoben werden — es ist bereits zugeteilt.');
      } else {
        setQueueOrder(before);
        setError(r.conflict.hint || 'Reihenfolge konnte nicht gesetzt werden.');
      }
    } catch (e) {
      setQueueOrder(before);
      setError(e instanceof Error ? e.message : 'Reihenfolge konnte nicht gesetzt werden.');
    } finally {
      setReordering(false);
      setDragId(null);
    }
  };

  return (
    <div className="agent-issues">
      <div className="ai-head">
        <span className="ai-title">Issues · {sessionName}</span>
        <span className="ai-count">{items.length} offen</span>
        <button className="ai-x" onClick={() => void load()} title="Aktualisieren"><RefreshCw size={13} /></button>
        <button className="ai-x" onClick={onClose} aria-label="Schließen"><X size={14} /></button>
      </div>

      {flash && <div className="ai-flash">{flash}</div>}
      {loading && <div className="ai-empty"><Loader2 size={14} className="ai-spin" /> Lädt…</div>}
      {!loading && error && <div className="ai-empty ai-err">{error}</div>}
      {!loading && !error && items.length === 0 && <div className="ai-empty">Keine offenen Issues für diesen Agenten.</div>}

      <div className="ai-list">
        {ordered.map((i) => (
          <div
            className={`ai-issue${dragId === i.id ? ' ai-dragging' : ''}${isQueued(i.id) ? ' ai-sortable' : ''}`}
            key={i.id}
            draggable={isQueued(i.id) && !reordering}
            onDragStart={() => setDragId(i.id)}
            onDragEnd={() => setDragId(null)}
            onDragOver={(e) => { if (dragId != null && isQueued(i.id)) e.preventDefault(); }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId != null && isQueued(i.id)) void applyReorder(dragId, i.id);
            }}
          >
            <div className="ai-issue-head">
              <div className="ai-issue-main">
                {isQueued(i.id) && (
                  <span className="ai-grip" title="Ziehen, um die Abarbeitungsreihenfolge zu ändern">
                    <GripVertical size={12} />
                    <span className="ai-pos">{queueOrder.indexOf(i.id) + 1}</span>
                  </span>
                )}
                <span className="ai-issue-id">#{i.id}</span>
                <span className="ai-issue-title">{i.title}</span>
                {i.metadata_json?.escalated_at && (
                  <span className="ai-tag ai-escal" title={`Eskaliert seit ${i.metadata_json.escalated_at}${i.metadata_json.escalation_target ? ` → ${i.metadata_json.escalation_target}` : ''}`}>⚠ eskaliert</span>
                )}
                {i.stages && i.stages.length > 0 && (
                  <span className="ai-stages" title={i.stages.map((s) => `${s.name}: ${s.status}`).join(' · ')}>
                    {i.stages.map((s) => (
                      <span key={s.name} className={`ai-stage ai-stage-${s.status}`}>{STAGE_SYM[s.status] || '○'}</span>
                    ))}
                  </span>
                )}
              </div>
              {/* Status + actions top-right — not a separate row under the title (Alex,
                  Issue #193): saves vertical space. */}
              <div className="ai-issue-right">
                {i.dispatch_status === 'active' && <span className="ai-tag ai-active">aktiv</span>}
                {isReallyQueued(i) && <span className="ai-tag ai-queued">wartend</span>}
                <div className="ai-actions">
                  <button
                    className="ai-act ai-push"
                    disabled={busyId === i.id || i.dispatch_status === 'active'}
                    onClick={() => void push(i)}
                    title={i.dispatch_status === 'active' ? 'Läuft bereits — wird gerade abgearbeitet' : 'An den Agenten pushen — jetzt abarbeiten'}
                  >
                    <Send size={12} /> {i.dispatch_status === 'active' ? 'Läuft' : 'Pushen'}
                  </button>
                  {/* While it's running, "Läuft" is disabled — Erinnern lets you nudge the
                      agent to keep going (Issue #195). Only shown when active. */}
                  {i.dispatch_status === 'active' && (
                    <button
                      className="ai-act ai-remind"
                      disabled={busyId === i.id}
                      onClick={() => void remind(i)}
                      title={remindPrompt(i)}
                    >
                      <Bell size={12} /> Erinnern
                    </button>
                  )}
                  <button className="ai-act ai-done" disabled={busyId === i.id} onClick={() => void done(i)} title="Als erledigt markieren (resolve)">
                    <Check size={13} />
                  </button>
                  <button className="ai-act ai-drop" disabled={busyId === i.id} onClick={() => void drop(i)} title="Verwerfen (wontfix)">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
            {/* The FULL prompt the agent receives on Pushen — sent 1:1 via sendInput,
                no Prompt-Agent shortening. No truncation so Alex can verify it (#192/#193). */}
            <div className="ai-prompt" title="Diese Nachricht geht beim Pushen 1:1 an den Agenten">{pushPrompt(i)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
